/**
 * PostEventMomentumScreen
 * ========================
 * Captures post-event emotional momentum and presents upgrade CTAs.
 * 
 * TRIGGER CONDITIONS (LOCKED):
 * - Event ends naturally (status → ended)
 * - User leaves event early (voluntary exit)
 * - User's 60-minute event cap is reached
 * 
 * ONLY for ticketed events. Never for non-ticketed, lounge, or regular navigation.
 * 
 * COPY LOCKED - Do not modify without approval.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Crown, Sparkles } from "lucide-react";
import { AddToCalendarLink } from "./AddToCalendar";
import { track } from "../utils/analytics";

export default function PostEventMomentumScreen({ 
  eventId, 
  eventType = "video", 
  wasTicketed = true,
  exitReason = "ended", // "ended" | "early_exit" | "minutes_exhausted"
  onDismiss,
}) {
  const navigate = useNavigate();
  const [canDismiss, setCanDismiss] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Enable dismiss after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setCanDismiss(true);
    }, 3000);

    // Track screen shown
    track("post_event_momentum_shown", {
      event_id: eventId,
      event_type: eventType,
      was_ticketed: wasTicketed,
      exit_reason: exitReason,
    });

    return () => clearTimeout(timer);
  }, [eventId, eventType, wasTicketed, exitReason]);

  const handleUpgradeElite = useCallback(() => {
    track("post_event_upgrade_clicked", {
      event_id: eventId,
      upgrade_type: "elite",
    });
    navigate("/shop?highlight=elite");
  }, [eventId, navigate]);

  const handleJoinInnerCircle = useCallback(() => {
    track("post_event_upgrade_clicked", {
      event_id: eventId,
      upgrade_type: "inner_circle",
    });
    navigate("/shop?highlight=inner_circle");
  }, [eventId, navigate]);

  const handleNotNow = useCallback(() => {
    track("post_event_momentum_dismissed", {
      event_id: eventId,
    });
    if (onDismiss) {
      onDismiss();
    } else {
      navigate("/dashboard");
    }
  }, [eventId, navigate, onDismiss]);

  // Don't render if not visible or not ticketed
  if (!isVisible || !wasTicketed) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm">
      {/* Subtle gradient accent - warm tones, NOT purple ticket gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-900/20 via-black to-amber-900/10 pointer-events-none" />
      
      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Sparkles className="absolute top-1/4 left-1/4 w-6 h-6 text-rose-500/20 animate-pulse" />
        <Sparkles className="absolute top-1/3 right-1/3 w-4 h-4 text-amber-500/20 animate-pulse delay-300" />
        <Sparkles className="absolute bottom-1/3 left-1/3 w-5 h-5 text-rose-500/15 animate-pulse delay-500" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-md mx-auto px-6 text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-rose-500/20 to-amber-500/20 flex items-center justify-center border border-white/10">
            <Sparkles className="w-10 h-10 text-white/80" />
          </div>
        </div>

        {/* Headline - COPY LOCKED */}
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          I know — that felt different.
        </h1>

        {/* Sub-copy - COPY LOCKED */}
        <p className="text-lg text-white/70 mb-10 leading-relaxed">
          You just experienced one of the ways people connect on UnityLove. Want to keep this level of connection going?
        </p>

        {/* CTAs */}
        <div className="space-y-4">
          {/* Primary CTA - Upgrade to Elite */}
          <button
            onClick={handleUpgradeElite}
            className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-amber-500 to-rose-500 text-white font-semibold text-lg flex items-center justify-center gap-3 hover:from-amber-600 hover:to-rose-600 transition-all shadow-lg shadow-rose-500/20"
          >
            <Star className="w-5 h-5" />
            Upgrade to Elite
          </button>

          {/* Secondary CTA - Join Inner Circle */}
          <button
            onClick={handleJoinInnerCircle}
            className="w-full py-4 px-6 rounded-xl bg-white/10 border border-white/20 text-white font-semibold text-lg flex items-center justify-center gap-3 hover:bg-white/15 hover:border-white/30 transition-all"
          >
            <Crown className="w-5 h-5 text-amber-400" />
            Join Inner Circle
          </button>
        </div>

        {/* Calendar CTA - Cherry #3 */}
        <div className="mt-6">
          <AddToCalendarLink eventId={eventId} />
        </div>

        {/* Tertiary option - Not right now */}
        {canDismiss ? (
          <button
            onClick={handleNotNow}
            className="mt-8 text-white/50 hover:text-white/70 text-sm transition-colors"
          >
            Not right now
          </button>
        ) : (
          <div className="mt-8 h-5" /> // Placeholder to maintain layout
        )}
      </div>
    </div>
  );
}

/**
 * Hook to manage post-event momentum screen display
 */
export function usePostEventMomentum() {
  const [showMomentum, setShowMomentum] = useState(false);
  const [momentumData, setMomentumData] = useState(null);

  const triggerMomentumScreen = useCallback((data) => {
    // Only trigger for ticketed events
    if (!data.wasTicketed) {
      console.log("[PostEventMomentum] Skipped - not a ticketed event");
      return;
    }

    setMomentumData({
      eventId: data.eventId,
      eventType: data.eventType || "video",
      wasTicketed: data.wasTicketed,
      exitReason: data.exitReason || "ended",
    });
    setShowMomentum(true);
  }, []);

  const dismissMomentumScreen = useCallback(() => {
    setShowMomentum(false);
    setMomentumData(null);
  }, []);

  return {
    showMomentum,
    momentumData,
    triggerMomentumScreen,
    dismissMomentumScreen,
  };
}
